/**
 * Higher-order component (HOC) for image handling.
 * Wraps image components with error handling, loading animations, and default image fallback.
 * Provides consistent image behavior across the application.
 */

import AnimationProps from '@shared/types/props/animation';
import { SyntheticEvent } from 'react';
import { TRANSITION } from '@shared/types/constants/animations';

/**
 * Image props.
 */
interface Props extends AnimationProps {
  className?: string;
  src?: string;
  onerror?: (target: HTMLImageElement) => void;
}

/**
 * Higher-order component that enhances image components with error handling and animations.
 * Wraps an image element with onError and onLoad handlers, opacity transitions, and default image fallback.
 *
 * @param {React.ElementType} ImageElement - The image component to wrap
 * @param {boolean} animation - Whether to enable opacity transition animations
 * @param {string} imgDefault - Default image URL to use on error
 * @param {string} defaultOpacity - Default opacity value for the image
 * @returns {function} Enhanced image component with error handling and animations
 */
export default function withImage(
  ImageElement: React.ElementType,
  animation = true,
  imgDefault = '',
  defaultOpacity = '1'
) {
  const onError = (event: SyntheticEvent, props: Props) => {
    const target = event.target as HTMLImageElement;
    if (props.onerror) {
      props.onerror(target);
    } else {
      target.src = imgDefault;
    }
  };
  const onLoad = (event: SyntheticEvent) => {
    const target = event.target as HTMLImageElement;
    setTimeout(() => {
      target.style.opacity =
        parseFloat(target.style.opacity) > 0
          ? target.style.opacity
          : defaultOpacity;
    }, 100);
  };

  return (props: Props) => {
    return (
      <ImageElement
        className={props.className || ''}
        draggable={false}
        loading='lazy'
        onError={(event: React.SyntheticEvent<HTMLImageElement, Event>) =>
          onError(event, props)
        }
        onLoad={onLoad}
        style={{
          opacity: props.opacity || 0,
          transition: animation ? TRANSITION.OPACITY_TRANSITION : undefined,
        }}
        {...props}
      />
    );
  };
}
